import theme from "@shared/utils/themeColors";
import React, { useEffect, useRef, useState } from "react";
import Transition from "../../../utils/Transition";
import { useNotificationsContext } from "../../context/NotificationsContext";
import { NotificationItem } from "./NotificationItem";

interface NotificationsDropdownProps {
  align?: "left" | "right";
}

export const NotificationsDropdown: React.FC<NotificationsDropdownProps> = ({
  align = "right",
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const dropdown = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLUListElement>(null);

  const {
    notifications,
    unreadCount,
    loading,
    hasMore,
    loadMore,
    isLoadingMore,
    markAllAsRead,
    lastNotification,
  } = useNotificationsContext();

  // Handle new notifications
  /* useEffect(() => {
    if (lastNotification.current) {
      const notification = lastNotification.current;
      console.log(notification);
      toast.custom((t) => <NotificationToast notification={notification} />, {
        id: notification.id,
        duration: 8000,
        position: "top-right",
      });
      lastNotification.current = null;
    }
  }, [lastNotification]); */

  // Close on click outside
  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target as Node) ||
        trigger.current?.contains(target as Node)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  // Close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  const handleDropdownOpen = () => {
    setDropdownOpen(!dropdownOpen);
    if (!dropdownOpen && unreadCount > 0) {
      markAllAsRead();
    }
  };

  return (
    <div className="relative inline-flex">
      <button
        ref={trigger}
        className={`w-8 h-8 flex items-center justify-center hover:bg-gray-100 lg:hover:bg-gray-200 dark:hover:bg-gray-700/50 dark:lg:hover:bg-gray-800 rounded-full ${
          dropdownOpen && "bg-gray-200 dark:bg-gray-800"
        }`}
        aria-haspopup="true"
        onClick={handleDropdownOpen}
        aria-expanded={dropdownOpen}
      >
        <span className="sr-only">Notifications</span>
        <svg
          className={`fill-current ${theme.text.muted}`}
          width={16}
          height={16}
          viewBox="0 0 18 18"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g strokeWidth="2" fill="none" stroke="currentColor">
            <path
              d="M15.75,13.25c-1.105,0-2-.895-2-2V6.5c0-2.623-2.127-4.75-4.75-4.75h0c-2.623,0-4.75,2.127-4.75,4.75v4.75c0,1.105-.895,2-2,2H15.75Z"
              strokeLinecap="round"
              strokeLinejoin="round"
            ></path>
            <path
              d="M10.588,15.185c-.095-.117-.237-.185-.388-.185h-2.399c-.151,0-.293,.068-.388,.185-.095,.117-.132,.271-.101,.418,.173,.822,.868,1.397,1.689,1.397s1.516-.575,1.689-1.397c.031-.147-.006-.301-.101-.418Z"
              stroke="none"
              fill="currentColor"
            ></path>
          </g>
        </svg>
        {unreadCount > 0 && (
          <div
            className={`absolute top-0 right-0 w-2.5 h-2.5 ${theme.bg.error} border-2 border-gray-100 dark:border-gray-900 rounded-full`}
          ></div>
        )}
      </button>

      <Transition
        className={`origin-top-right z-10 absolute top-full -mr-48 sm:mr-0 min-w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/60 py-1.5 rounded-lg shadow-lg overflow-hidden mt-1 ${
          align === "right" ? "right-0" : "left-0"
        }`}
        show={dropdownOpen}
        enter="transition ease-out duration-200 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-200"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
        appear={undefined}
      >
        <div
          ref={dropdown}
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => setDropdownOpen(false)}
        >
          <div
            className={`text-xs font-semibold ${theme.text.inactive} uppercase pt-1.5 pb-2 px-4`}
          >
            Notifications
          </div>
          <ul ref={notificationsRef} className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <li className="text-center py-4">
                <div className="animate-pulse">
                  <div
                    className={`h-4 ${theme.bg.tertiary} rounded w-3/4 mx-auto mb-2`}
                  ></div>
                  <div
                    className={`h-4 ${theme.bg.tertiary} rounded w-1/2 mx-auto`}
                  ></div>
                </div>
              </li>
            ) : notifications.length > 0 ? (
              <>
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onClose={() => setDropdownOpen(false)}
                  />
                ))}
                {hasMore && (
                  <li className="text-center py-2">
                    <button
                      onClick={() => loadMore()}
                      disabled={isLoadingMore}
                      className={`text-sm ${theme.text.info} hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300`}
                    >
                      {isLoadingMore ? "Loading..." : "Load more"}
                    </button>
                  </li>
                )}
              </>
            ) : (
              <li className={`text-center py-4 ${theme.text.muted}`}>
                No notifications
              </li>
            )}
          </ul>
        </div>
      </Transition>
    </div>
  );
};
